import React, { useState } from 'react';
import {
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import InputMask from 'react-input-mask';
import { format } from 'date-fns';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import useApi from '../../utils/useApi';
import FormInputDate from '../UI/Form/InputDate/FormInputDate';
import * as Yup from 'yup';
import UIDialogConfirm from '../UI/Dialog/Confirm/UIDialogConfirm';
import { fetchCepInfo } from '../../utils/brasilApi';

const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('Nome é obrigatório'),
    lastName: Yup.string(),
    cpf: Yup.string(),
    bornIn: Yup.date().nullable(),
    email: Yup.string().email('Digite um e-mail válido'),
});

const ClientCreateModal = ({ open, onClose, initialName = '', onSuccess }) => {
    const [showFullFields, setShowFullFields] = useState(false);
    const [cepLoading, setCepLoading] = useState(false);
    const [cepError, setCepError] = useState('');
    const [alert, setAlert] = useState(false);

    const [createLoad, createInfo, setCreateInfo] = useApi({
        method: 'post',
        url: '/customers',
    });

    const modalInitialState = {
        name: initialName || '',
        lastName: '',
        cpf: '',
        rg: '',
        bornIn: null,
        email: '',
        gender: 3,
        marialStatus: 1,
        children: 0,
        whatsapp: '',
        cep: '',
        city: '',
        neighborhood: '',
        street: '',
        houseNumber: '',
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="client-create-dialog-title"
        >
            <Formik
                initialValues={modalInitialState}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={(values, { setSubmitting }) => {
                    const cleanWhatsapp = (values.whatsapp || '').replace(/\D/g, '');
                    const cleanCpf = (values.cpf || '').replace(/\D/g, '');
                    const valuesClient = {
                        ...values,
                        name: values.name.trim(),
                        lastName: values.lastName ? values.lastName.trim() : '',
                        whatsapp: cleanWhatsapp,
                        cpf: cleanCpf,
                        bornIn: values.bornIn ? format(new Date(values.bornIn), 'yyyy-MM-dd') : undefined,
                    };

                    createLoad({
                        data: valuesClient,
                        onCompleted: (res) => {
                            setSubmitting(false);
                            setCreateInfo(null);
                            if (res && res.data) {
                                const newId = res.data.id || (typeof res.data === 'object' && res.data.id);
                                const createdObj = {
                                    ...valuesClient,
                                    id: newId,
                                    phone: cleanWhatsapp,
                                };
                                if (onSuccess) {
                                    onSuccess(createdObj);
                                }
                                if (onClose) {
                                    onClose();
                                }
                            }
                        },
                        onError: (err) => {
                            setSubmitting(false);
                            setCreateInfo({
                                error: err,
                                message: 'Falha ao criar cliente. Verifique os dados.',
                            });
                            setAlert(true);
                        },
                    });
                }}
            >
                {({ errors, touched, isSubmitting, setFieldValue, values }) => (
                    <Form>
                        <DialogTitle id="client-create-dialog-title" style={{ paddingBottom: 4 }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <span style={{ fontWeight: 700, fontSize: '1.2rem', color: '#17497F' }}>
                                    Novo Cliente
                                </span>
                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                    Cadastro ágil para venda e comanda
                                </span>
                            </div>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                {/* Campos Rápidos Essenciais */}
                                <Grid item xs={12} sm={7}>
                                    <Field
                                        name="name"
                                        label="Nome *"
                                        autoFocus
                                        error={Boolean(errors.name && touched.name)}
                                        helperText={touched.name && errors.name}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        as={TextField}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                    <Field
                                        name="lastName"
                                        label="Sobrenome"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        as={TextField}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputMask
                                        value={values.whatsapp}
                                        mask="(99) 9 9999-9999"
                                        onChange={(ev) =>
                                            setFieldValue(
                                                'whatsapp',
                                                ev.target.value.replace(/[^0-9]/g, ''),
                                            )
                                        }
                                    >
                                        {() => (
                                            <TextField
                                                label="WhatsApp / Telefone"
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                placeholder="(00) 0 0000-0000"
                                            />
                                        )}
                                    </InputMask>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputMask
                                        value={values.cpf}
                                        mask="999.999.999-99"
                                        onChange={(ev) =>
                                            setFieldValue(
                                                'cpf',
                                                ev.target.value.replace(/[^0-9]/g, ''),
                                            )
                                        }
                                    >
                                        {() => (
                                            <TextField
                                                label="CPF"
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                                placeholder="000.000.000-00"
                                            />
                                        )}
                                    </InputMask>
                                </Grid>

                                {/* Botão para expandir campos completos */}
                                <Grid item xs={12}>
                                    <Button
                                        size="small"
                                        style={{ textTransform: 'none', color: '#17497F', padding: 0 }}
                                        onClick={() => setShowFullFields(!showFullFields)}
                                        endIcon={showFullFields ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    >
                                        {showFullFields ? 'Ocultar campos adicionais' : '+ Mais informações (Endereço, Nascimento, E-mail)'}
                                    </Button>
                                </Grid>

                                {/* Seção Expansível */}
                                <Grid item xs={12}>
                                    <Collapse in={showFullFields}>
                                        <Grid container spacing={2} style={{ paddingTop: 8 }}>
                                            <Grid item xs={12} sm={6}>
                                                <FormInputDate
                                                    value={values.bornIn}
                                                    clearable
                                                    label="Data de nascimento"
                                                    onChange={(value) => setFieldValue('bornIn', value)}
                                                    fullWidth
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth size="small" variant="outlined">
                                                    <InputLabel shrink id="client-modal-gender">Gênero</InputLabel>
                                                    <Field
                                                        name="gender"
                                                        labelId="client-modal-gender"
                                                        label="Gênero"
                                                        as={Select}
                                                    >
                                                        <MenuItem value={1}>Masculino</MenuItem>
                                                        <MenuItem value={2}>Feminino</MenuItem>
                                                        <MenuItem value={3}>Indefinido</MenuItem>
                                                    </Field>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Field
                                                    name="email"
                                                    label="E-mail"
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    error={Boolean(errors.email && touched.email)}
                                                    helperText={touched.email && errors.email}
                                                    as={TextField}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <InputMask
                                                    value={values.cep || ''}
                                                    mask="99999-999"
                                                    onChange={(ev) => {
                                                        const rawValue = ev.target.value;
                                                        setFieldValue('cep', rawValue);
                                                        const clean = rawValue.replace(/\D/g, '');
                                                        if (clean.length === 8) {
                                                            setCepLoading(true);
                                                            setCepError('');
                                                            fetchCepInfo(clean)
                                                                .then((data) => {
                                                                    if (data.street) setFieldValue('street', data.street);
                                                                    if (data.neighborhood) setFieldValue('neighborhood', data.neighborhood);
                                                                    if (data.city) setFieldValue('city', data.city);
                                                                })
                                                                .catch(() => {
                                                                    setCepError('CEP não encontrado');
                                                                })
                                                                .finally(() => setCepLoading(false));
                                                        }
                                                    }}
                                                >
                                                    {() => (
                                                        <TextField
                                                            label="CEP"
                                                            placeholder="00000-000"
                                                            fullWidth
                                                            variant="outlined"
                                                            size="small"
                                                            error={Boolean(errors.cep || cepError)}
                                                            helperText={
                                                                cepLoading
                                                                    ? 'Buscando CEP...'
                                                                    : cepError || ''
                                                            }
                                                            InputProps={{
                                                                endAdornment: cepLoading ? <CircularProgress size={16} /> : null,
                                                            }}
                                                        />
                                                    )}
                                                </InputMask>
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <Field
                                                    name="street"
                                                    label="Rua / Avenida"
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    as={TextField}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={5}>
                                                <Field
                                                    name="neighborhood"
                                                    label="Bairro"
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    as={TextField}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Field
                                                    name="city"
                                                    label="Cidade"
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    as={TextField}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <Field
                                                    name="houseNumber"
                                                    label="Número"
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    as={TextField}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Collapse>
                                </Grid>

                                {createInfo && createInfo.error && (
                                    <Grid item xs={12}>
                                        <Typography color="error" variant="caption">
                                            {createInfo.message || 'Erro ao cadastrar cliente.'}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </DialogContent>
                        <DialogActions style={{ padding: '12px 24px', justifyContent: 'space-between' }}>
                            <Button onClick={onClose} color="default" disabled={isSubmitting}>
                                Cancelar [Esc]
                            </Button>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={isSubmitting || (createLoad && createLoad.loading)}
                                style={{ backgroundColor: '#17497F', color: '#fff', fontWeight: 600, minWidth: 140 }}
                            >
                                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : 'Salvar Cliente'}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
            <UIDialogConfirm
                open={alert}
                close={() => setAlert(false)}
                data={createInfo}
                messageError={createInfo && createInfo.message}
                type="error"
            />
        </Dialog>
    );
};

export default ClientCreateModal;
